import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { api } from "@/lib/api";
import { setAuthMode, migrateGuestData } from "@/lib/offlineStore";
import { toast } from "sonner";

/**
 * AuthContext — single source of truth for "is this browser signed in?".
 *
 * Three states on `authState`:
 *   - null    → still checking the server
 *   - "guest" → not signed in; app runs in local-only mode (no backend sync)
 *   - "user"  → signed in; backend sync is enabled
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Sign-in uses self-hosted Google OAuth with the app's own Client ID. The
 * popup flow (`ux_mode: "popup"`) opens Google's consent in a new window,
 * exchanges the auth code via our backend, and sets a session cookie — the
 * user never leaves the Mango Notes tab.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authState, setAuthState] = useState(null); // null | "guest" | "user"

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setAuthState("user");
      setAuthMode("user");
    } catch {
      setUser(null);
      setAuthState("guest");
      setAuthMode("guest");
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Self-hosted Google OAuth (authorization-code flow, popup UX).
  // Scope is only `openid email` — we never request `profile` so Google
  // doesn't send us name or picture.
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "popup",
    scope: "openid email",
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    redirect_uri: typeof window !== "undefined" ? window.location.origin : undefined,
    onSuccess: async (resp) => {
      try {
        const { data } = await api.post("/auth/google", {
          code: resp.code,
          redirect_uri: window.location.origin,
        });
        setUser(data);
        setAuthState("user");
        setAuthMode("user");
        toast.success(`Signed in as ${data.email}`);
        try {
          const { uploaded } = await migrateGuestData();
          if (uploaded > 0) {
            toast.success(`Backed up ${uploaded} note${uploaded > 1 ? "s" : ""} to your account`);
          }
        } catch (err) {
          console.warn("[auth] guest-data migration failed:", err?.message || err);
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "Please try again";
        toast.error(`Sign-in failed — ${detail}`);
      }
    },
    onError: (err) => {
      console.warn("[auth] Google OAuth error:", err);
      toast.error("Sign-in was cancelled or failed");
    },
  });

  const signIn = () => googleLogin();

  const signOut = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server-side session cleanup is best-effort — the cookie is cleared
      // and the local reload below still logs the user out of this device.
      console.warn("[auth] logout request failed:", err?.message || err);
    }
    setUser(null);
    setAuthState("guest");
    setAuthMode("guest");
    // Wipe local cache so the next visitor sees a blank app.
    try {
      if (typeof indexedDB !== "undefined" && indexedDB.deleteDatabase) {
        indexedDB.deleteDatabase("keepflow");
      }
      ["keepflow.notes", "keepflow.labels", "keepflow.pendingOps", "keepflow.lastSync"].forEach((k) => {
        try { localStorage.removeItem(k); } catch (err) {
          console.warn(`[auth] could not clear ${k}:`, err?.message || err);
        }
      });
    } catch (err) {
      console.warn("[auth] local cache wipe failed:", err?.message || err);
    }
    window.location.reload();
  };

  return (
    <AuthContext.Provider value={{
      user, authState, signIn, signOut, refresh: checkAuth,
      setAuthState: (m) => { setAuthMode(m); setAuthState(m); },
      setUser, migrateGuestData,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
